import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState, useEffect } from "react";
import { 
  DEFAULT_CURRENCIES, 
  getUserBaseCurrency, 
  convertCurrency,
  fetchCurrencies,
  addCurrency,
  toggleCurrencyStatus,
  type Currency
} from "@/lib/currency-utils";

export function useCurrency() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  
  // Get all available currencies from the API
  const { 
    data: currencies = DEFAULT_CURRENCIES,
    isLoading: isCurrenciesLoading
  } = useQuery({
    queryKey: ["/api/currencies"],
    queryFn: async () => {
      return await fetchCurrencies();
    }
  });

  // Get the user's preferred currency
  const {
    data: baseCurrency = "USD",
    isLoading: isBaseCurrencyLoading
  } = useQuery({
    queryKey: ["/api/currency-settings"],
    queryFn: async () => {
      try {
        const data = await apiRequest("GET", "/api/currency-settings");
        return data.baseCurrency || "USD";
      } catch (error) {
        console.error("Error fetching currency settings:", error);
        return "USD";
      }
    }
  });

  // Mutation to update the user's preferred currency
  const updateBaseCurrencyMutation = useMutation({
    mutationFn: async (newBaseCurrency: string) => {
      return await apiRequest("POST", "/api/currency-settings", { baseCurrency: newBaseCurrency });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency-settings"] });
    }
  });

  // Mutation to add a new currency
  const addCurrencyMutation = useMutation({
    mutationFn: async ({ code, name }: { code: string, name: string }) => {
      return await addCurrency(code, name);
    },
    onSuccess: () => {
      // Invalidate currencies query to refetch
      queryClient.invalidateQueries({ queryKey: ["/api/currencies"] });
    }
  });

  // Mutation to toggle currency active status
  const toggleCurrencyStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number, isActive: boolean }) => {
      return await toggleCurrencyStatus(id, isActive);
    },
    onSuccess: () => {
      // Invalidate currencies query to refetch
      queryClient.invalidateQueries({ queryKey: ["/api/currencies"] });
    }
  });
  
  // Helper function to convert an amount from one currency to another
  const convert = async (amount: number, fromCurrency: string, toCurrency?: string) => {
    const targetCurrency = toCurrency || baseCurrency;
    if (fromCurrency === targetCurrency) return amount;
    
    try {
      return await convertCurrency(amount, fromCurrency, targetCurrency);
    } catch (error) {
      console.error("Error converting currency:", error);
      return amount; // Return the original amount if conversion fails
    }
  };
  
  // Helper function to format an amount in the user's preferred currency
  const formatInBaseCurrency = (amount: number, currencyCode: string) => {
    const activeCurrencies = currencies.filter(c => c.isActive);
    const currency = activeCurrencies.find(c => c.code === currencyCode) || 
                     activeCurrencies.find(c => c.code === baseCurrency) || 
                     activeCurrencies[0] || 
                     DEFAULT_CURRENCIES[0];
    
    return `${currency.symbol}${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} ${currency.code}`;
  };
  
  // Helper function to update the user's preferred currency
  const setBaseCurrency = (newBaseCurrency: string) => {
    updateBaseCurrencyMutation.mutate(newBaseCurrency);
  };
  
  // Monitor loading state
  useEffect(() => {
    if (!isBaseCurrencyLoading && !isCurrenciesLoading) {
      setLoading(false);
    }
  }, [isBaseCurrencyLoading, isCurrenciesLoading]);
  
  return {
    // Basic currency data
    baseCurrency,
    currencies,
    activeCurrencies: currencies.filter(c => c.isActive),
    
    // Currency operations
    setBaseCurrency,
    addCurrency: addCurrencyMutation.mutate,
    toggleCurrencyStatus: toggleCurrencyStatusMutation.mutate,
    
    // Helper functions
    convert,
    formatInBaseCurrency,
    
    // Status
    isLoading: loading || isBaseCurrencyLoading || isCurrenciesLoading,
    isUpdating: updateBaseCurrencyMutation.isPending,
    isAddingCurrency: addCurrencyMutation.isPending,
    isTogglingCurrency: toggleCurrencyStatusMutation.isPending
  };
}